import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Pencil,
  Plus,
  Trash2,
  Waves,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

import {
  createGearSet,
  deleteGearSet,
  listGearSets,
  updateGearSet,
} from "../api";
import { trackProductEvent } from "../analytics/trackEvent";
import { getActiveAwareAuthToken } from "../auth/bootstrapTokens";
import {
  getGearModelSuggestions,
  getHookSuggestions,
  getLineSuggestions,
  getLureOrBaitSuggestions,
  getReelSuggestions,
  getRodSuggestions,
} from "../components/CreatePostModal/catch-form/catchFormGearHelpers";
import useCreatePostFormResources from "../components/CreatePostModal/useCreatePostFormResources";
import DesktopRightRail from "../components/DesktopRightRail";
import "../styles/pages/MyGear.css";

const EMPTY_FORM = {
  name: "",
  rod: "",
  reel: "",
  line: "",
  leader: "",
  lure: "",
  hook: "",
  notes: "",
};

const FIELD_LIMITS = {
  name: 80,
  rod: 160,
  reel: 160,
  line: 160,
  leader: 160,
  lure: 160,
  hook: 160,
  notes: 500,
};

const FIELD_KEYS = ["rod", "reel", "line", "leader", "lure", "hook", "notes"];
const TACKLE_FIELD_KEYS = ["lure", "hook"];
const GEAR_ICON_PATHS = {
  gearSet: "/icons/gear/gear-set.png",
  hook: "/icons/gear/hook.png",
  leader: "/icons/gear/leader.png",
  line: "/icons/gear/line-braid.png",
  lure: "/icons/gear/lure.png",
  reel: "/icons/gear/reel.png",
  rod: "/icons/gear/rod.png",
  tackleBox: "/icons/gear/tackle-box.png",
};
const FIELD_ICON_TYPES = {
  rod: "rod",
  reel: "reel",
  line: "line",
  leader: "leader",
  lure: "lure",
  hook: "hook",
};

const CATEGORY_FILTERS = [
  { key: "all", labelKey: "my_gear_filter_all" },
  { key: "saved", labelKey: "my_gear_filter_saved_sets" },
  { key: "rod", labelKey: "my_gear_filter_rods" },
  { key: "reel", labelKey: "my_gear_filter_reels" },
  { key: "tackle", labelKey: "my_gear_filter_tackle" },
];
const SHOW_GEAR_FILTERS = false;

function normalizeGearSet(rawGearSet) {
  return {
    id: String(rawGearSet?.id || ""),
    userId: String(rawGearSet?.userId || ""),
    name: String(rawGearSet?.name || ""),
    rod: String(rawGearSet?.rod || ""),
    reel: String(rawGearSet?.reel || ""),
    line: String(rawGearSet?.line || ""),
    leader: String(rawGearSet?.leader || ""),
    lure: String(rawGearSet?.lure || ""),
    hook: String(rawGearSet?.hook || ""),
    notes: String(rawGearSet?.notes || ""),
    isDefault: Boolean(rawGearSet?.isDefault),
    createdAt: String(rawGearSet?.createdAt || ""),
    updatedAt: String(rawGearSet?.updatedAt || ""),
  };
}

function getVisibleFields(gearSet) {
  return FIELD_KEYS.map((fieldKey) => ({
    key: fieldKey,
    value: String(gearSet?.[fieldKey] || "").trim(),
  })).filter((field) => field.value);
}

function hasGearValue(value) {
  return String(value || "").trim().length > 0;
}

function countGearSetTackleItems(gearSet) {
  return TACKLE_FIELD_KEYS.filter((fieldKey) => hasGearValue(gearSet?.[fieldKey]))
    .length;
}

function buildSavePayload(formState) {
  return Object.fromEntries(
    Object.entries(formState).map(([key, value]) => [key, String(value || "").trim()])
  );
}

function normalizeSuggestionValue(suggestion) {
  if (typeof suggestion === "string") return suggestion;
  return String(suggestion?.value || "");
}

function getGearSetAnalyticsMetadata(payload, source) {
  return {
    source,
    has_rod: Boolean(payload?.rod),
    has_reel: Boolean(payload?.reel),
    has_line: Boolean(payload?.line),
    has_leader: Boolean(payload?.leader),
    has_lure: Boolean(payload?.lure),
    has_hook: Boolean(payload?.hook),
    has_notes: Boolean(payload?.notes),
  };
}

function GearIcon({ type, className = "" }) {
  const iconPath = GEAR_ICON_PATHS[type];
  if (!iconPath) return null;

  return (
    <img
      src={iconPath}
      alt=""
      className={`my-gear-png-icon${className ? ` ${className}` : ""}`}
      aria-hidden="true"
    />
  );
}

export default function MyGearPage({
  currentUser,
  posts = [],
  users = [],
  onOpenUser,
  enableDesktopRightRail = false,
  desktopRightRailTargetId,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const nameInputRef = useRef(null);
  const shouldFocusEditFormRef = useRef(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [gearSets, setGearSets] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [formMode, setFormMode] = useState(null);
  const [editingId, setEditingId] = useState("");
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [activeSuggestionField, setActiveSuggestionField] = useState("");
  const [formError, setFormError] = useState("");
  const [actionError, setActionError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [pendingDeleteGearSet, setPendingDeleteGearSet] = useState(null);
  const [desktopRightRailTarget, setDesktopRightRailTarget] = useState(null);
  const gearResources = useCreatePostFormResources({
    enabled: Boolean(formMode),
    includeGearData: true,
    includeHookBrands: true,
    includeHookSizes: false,
    includeBaitData: true,
    includeFishingMethods: false,
    includeLureSizes: false,
    includeFishList: false,
  });

  const authToken = useMemo(
    () => (typeof window !== "undefined" ? getActiveAwareAuthToken(window) : ""),
    []
  );

  const closeUtilityPage = useCallback(() => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      Number(window.history.state.idx) > 0
    ) {
      navigate(-1);
      return;
    }

    navigate("/", { replace: true });
  }, [navigate]);

  const gearStats = useMemo(() => {
    const countSetsWithField = (fieldKey) =>
      gearSets.filter((gearSet) => hasGearValue(gearSet?.[fieldKey])).length;

    return {
      sets: gearSets.length,
      rods: countSetsWithField("rod"),
      reels: countSetsWithField("reel"),
      lines: countSetsWithField("line"),
      tackle: gearSets.reduce(
        (total, gearSet) => total + countGearSetTackleItems(gearSet),
        0
      ),
    };
  }, [gearSets]);

  const filteredGearSets = useMemo(() => {
    if (activeFilter === "rod") {
      return gearSets.filter((gearSet) => String(gearSet?.rod || "").trim());
    }

    if (activeFilter === "reel") {
      return gearSets.filter((gearSet) => String(gearSet?.reel || "").trim());
    }

    if (activeFilter === "tackle") {
      return gearSets.filter((gearSet) => countGearSetTackleItems(gearSet) > 0);
    }

    return gearSets;
  }, [activeFilter, gearSets]);

  const loadGearSets = useCallback(
    async ({ signal } = {}) => {
      setIsLoading(true);
      setLoadError("");
      try {
        const loadedGearSets = await listGearSets(authToken || "", { signal });
        setGearSets(loadedGearSets.map(normalizeGearSet));
      } catch (error) {
        if (error?.name === "AbortError") return;
        setLoadError(t("my_gear_load_error"));
      } finally {
        setIsLoading(false);
      }
    },
    [authToken, t]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadGearSets({ signal: controller.signal });
    return () => controller.abort();
  }, [loadGearSets]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!desktopRightRailTargetId || typeof document === "undefined") {
      setDesktopRightRailTarget(null);
      return;
    }

    setDesktopRightRailTarget(
      document.getElementById(desktopRightRailTargetId)
    );
  }, [desktopRightRailTargetId]);

  useEffect(() => {
    if (!formMode || !isMobileViewport || typeof window === "undefined") {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    if (!body || !documentElement) return undefined;

    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      documentElement.scrollTop ||
      body.scrollTop ||
      0;
    const previousBodyStyles = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
    };
    const previousDocumentOverflow = documentElement.style.overflow;

    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    documentElement.style.overflow = "hidden";

    return () => {
      body.style.overflow = previousBodyStyles.overflow;
      body.style.position = previousBodyStyles.position;
      body.style.top = previousBodyStyles.top;
      body.style.left = previousBodyStyles.left;
      body.style.right = previousBodyStyles.right;
      body.style.width = previousBodyStyles.width;
      documentElement.style.overflow = previousDocumentOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [formMode, isMobileViewport]);

  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      if (formRef.current?.contains(event.target)) {
        return;
      }
      setActiveSuggestionField("");
    };

    document.addEventListener("mousedown", handlePointerDownOutside);
    return () => document.removeEventListener("mousedown", handlePointerDownOutside);
  }, []);

  useEffect(() => {
    if (
      formMode !== "edit" ||
      !editingId ||
      isMobileViewport ||
      !shouldFocusEditFormRef.current ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    shouldFocusEditFormRef.current = false;
    let focusTimeoutId;
    const frameId = window.requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      focusTimeoutId = window.setTimeout(() => {
        nameInputRef.current?.focus({ preventScroll: true });
      }, 180);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      if (focusTimeoutId) {
        window.clearTimeout(focusTimeoutId);
      }
    };
  }, [editingId, formMode, isMobileViewport]);

  const startCreate = (source = "my_gear_page") => {
    trackProductEvent("gear_set_create_opened", {
      featureArea: "my_gear",
      metadata: { source },
    });
    setFormMode("create");
    shouldFocusEditFormRef.current = false;
    setActiveFilter("all");
    setEditingId("");
    setFormState(EMPTY_FORM);
    setActiveSuggestionField("");
    setFormError("");
    setActionError("");
  };

  const startEdit = (gearSet) => {
    shouldFocusEditFormRef.current = true;
    setFormMode("edit");
    setEditingId(gearSet.id);
    setFormState({
      name: gearSet.name,
      rod: gearSet.rod,
      reel: gearSet.reel,
      line: gearSet.line,
      leader: gearSet.leader,
      lure: gearSet.lure,
      hook: gearSet.hook,
      notes: gearSet.notes,
    });
    setActiveSuggestionField("");
    setFormError("");
    setActionError("");
  };

  const closeForm = ({ force = false } = {}) => {
    if (isSaving && !force) return;
    setFormMode(null);
    setEditingId("");
    setFormState(EMPTY_FORM);
    setActiveSuggestionField("");
    setFormError("");
  };

  const handleFieldChange = (fieldKey, nextValue) => {
    const maxLength = FIELD_LIMITS[fieldKey] || 160;
    setFormState((current) => ({
      ...current,
      [fieldKey]: String(nextValue || "").slice(0, maxLength),
    }));
    setFormError("");
  };

  const getFieldSuggestions = (fieldKey) => {
    const input = formState[fieldKey];
    if (!input) return [];

    if (fieldKey === "rod") {
      return getRodSuggestions(gearResources.rodDatabase, input);
    }

    if (fieldKey === "reel") {
      return getReelSuggestions(gearResources.reelDatabase, input);
    }

    if (fieldKey === "line") {
      return getLineSuggestions(gearResources.braidDatabase, input);
    }

    if (fieldKey === "leader") {
      return getGearModelSuggestions(gearResources.leaderDatabase, input);
    }

    if (fieldKey === "lure") {
      return getLureOrBaitSuggestions(
        {
          lureDatabase: gearResources.lureDatabase,
          baitDatabase: gearResources.baitDatabase,
          liveBaitDatabase: gearResources.liveBaitDatabase,
          deadBaitDatabase: gearResources.deadBaitDatabase,
        },
        input
      );
    }

    if (fieldKey === "hook") {
      return getHookSuggestions(gearResources.hookBrandDatabase, input);
    }

    return [];
  };

  const handleSelectSuggestion = (fieldKey, suggestion) => {
    const suggestionValue = normalizeSuggestionValue(suggestion);
    if (!suggestionValue) return;
    handleFieldChange(fieldKey, suggestionValue);
    setActiveSuggestionField("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving) return;

    const payload = buildSavePayload(formState);
    if (!payload.name) {
      setFormError(t("my_gear_name_required"));
      return;
    }

    setIsSaving(true);
    setFormError("");
    setActionError("");
    try {
      const savedGearSet =
        formMode === "edit" && editingId
          ? await updateGearSet(authToken || "", editingId, payload)
          : await createGearSet(authToken || "", payload);
      const normalizedSavedGearSet = normalizeGearSet(savedGearSet);
      trackProductEvent(
        formMode === "edit" ? "gear_set_updated" : "gear_set_created",
        {
          featureArea: "my_gear",
          metadata: getGearSetAnalyticsMetadata(
            payload,
            formMode === "edit" ? "my_gear_edit_form" : "my_gear_create_form"
          ),
        }
      );

      setGearSets((currentGearSets) => {
        if (formMode === "edit") {
          return currentGearSets.map((gearSet) =>
            gearSet.id === normalizedSavedGearSet.id ? normalizedSavedGearSet : gearSet
          );
        }

        return [normalizedSavedGearSet, ...currentGearSets];
      });
      closeForm({ force: true });
    } catch (error) {
      setFormError(t("my_gear_save_error"));
    } finally {
      setIsSaving(false);
    }
  };

  const requestDelete = (gearSet) => {
    if (!gearSet?.id || deletingId) return;
    setActionError("");
    setPendingDeleteGearSet(gearSet);
  };

  const closeDeleteConfirm = () => {
    if (deletingId) return;
    setPendingDeleteGearSet(null);
  };

  const confirmDelete = async () => {
    const gearSet = pendingDeleteGearSet;
    if (!gearSet?.id || deletingId) return;

    setDeletingId(gearSet.id);
    setActionError("");
    try {
      await deleteGearSet(authToken || "", gearSet.id);
      trackProductEvent("gear_set_deleted", {
        featureArea: "my_gear",
        metadata: { source: "my_gear_card" },
      });
      setGearSets((currentGearSets) =>
        currentGearSets.filter((currentGearSet) => currentGearSet.id !== gearSet.id)
      );
      if (editingId === gearSet.id) {
        closeForm({ force: true });
      }
      setPendingDeleteGearSet(null);
    } catch (error) {
      setActionError(t("my_gear_delete_error"));
    } finally {
      setDeletingId("");
    }
  };

  const renderGearForm = ({ isSheet = false } = {}) => (
    <form
      ref={formRef}
      className={`my-gear-form${isSheet ? " my-gear-form--sheet" : ""}`}
      onSubmit={handleSubmit}
      role={isSheet ? "dialog" : undefined}
      aria-modal={isSheet ? "true" : undefined}
      aria-labelledby="my-gear-form-title"
    >
      <div className="my-gear-form__header">
        <h2 id="my-gear-form-title">
          {formMode === "edit" ? t("my_gear_edit_set") : t("my_gear_add_set")}
        </h2>
        {isSheet ? (
          <button
            type="button"
            className="my-gear-form__close"
            onClick={closeForm}
            disabled={isSaving}
            aria-label={t("common_close")}
          >
            <X size={18} strokeWidth={2.3} aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="my-gear-form__close my-gear-form__close--desktop"
            onClick={closeForm}
            disabled={isSaving}
            aria-label={t("common_close")}
          >
            <X size={18} strokeWidth={2.3} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="my-gear-form__body">
        <div className="my-gear-form__grid">
          <label className="my-gear-field my-gear-field--wide">
            <span>{t("my_gear_set_name")}</span>
            <input
              ref={nameInputRef}
              type="text"
              value={formState.name}
              maxLength={FIELD_LIMITS.name}
              onChange={(event) => handleFieldChange("name", event.target.value)}
            />
          </label>

          {FIELD_KEYS.map((fieldKey) => {
            const suggestions =
              activeSuggestionField === fieldKey ? getFieldSuggestions(fieldKey) : [];

            return (
              <label
                key={fieldKey}
                className={`my-gear-field${
                  fieldKey === "notes" ? " my-gear-field--wide" : ""
                }`}
              >
                <span>{t(`my_gear_${fieldKey}`)}</span>
                {fieldKey === "notes" ? (
                  <textarea
                    value={formState.notes}
                    maxLength={FIELD_LIMITS.notes}
                    rows={4}
                    onChange={(event) => handleFieldChange(fieldKey, event.target.value)}
                  />
                ) : (
                  <div className="my-gear-autocomplete">
                    <input
                      type="text"
                      value={formState[fieldKey]}
                      maxLength={FIELD_LIMITS[fieldKey]}
                      autoComplete="off"
                      onFocus={() => setActiveSuggestionField(fieldKey)}
                      onChange={(event) => {
                        setActiveSuggestionField(fieldKey);
                        handleFieldChange(fieldKey, event.target.value);
                      }}
                    />
                    {suggestions.length > 0 ? (
                      <ul className="my-gear-suggestions">
                        {suggestions.map((suggestion, index) => (
                          <li key={`${normalizeSuggestionValue(suggestion)}-${index}`}>
                            <button
                              type="button"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleSelectSuggestion(fieldKey, suggestion)}
                            >
                              <span>
                                {suggestion.displayName || suggestion.value || suggestion}
                              </span>
                              {suggestion.displaySubtitle ? (
                                <small>{suggestion.displaySubtitle}</small>
                              ) : null}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                )}
              </label>
            );
          })}
        </div>

        {formError ? <div className="my-gear-form__error">{formError}</div> : null}
      </div>

      <div className="my-gear-form__actions">
        {isSheet ? (
          <>
            <button type="button" className="my-gear-btn" onClick={closeForm} disabled={isSaving}>
              {t("common_close")}
            </button>
            <button type="submit" className="my-gear-btn my-gear-btn--primary" disabled={isSaving}>
              {t("my_gear_save_set")}
            </button>
          </>
        ) : (
          <>
            <button type="submit" className="my-gear-btn my-gear-btn--primary" disabled={isSaving}>
              {t("my_gear_save_set")}
            </button>
            <button type="button" className="my-gear-btn" onClick={closeForm} disabled={isSaving}>
              {t("my_gear_cancel")}
            </button>
          </>
        )}
      </div>
    </form>
  );

  const showDesktopRightRail = enableDesktopRightRail && !isMobileViewport;
  const desktopRightRail = showDesktopRightRail ? (
    <DesktopRightRail
      currentUser={currentUser}
      posts={posts}
      users={users}
      onOpenUser={onOpenUser}
    />
  ) : null;

  return (
    <div
      className={`my-gear-shell${
        showDesktopRightRail ? " my-gear-shell--with-right-rail" : ""
      }`}
    >
      <section className="my-gear-page">
      <header className="my-gear-header">
        <span className="my-gear-header__icon" aria-hidden="true">
          <GearIcon type="gearSet" className="my-gear-png-icon--header" />
        </span>
        <div className="my-gear-header__copy">
          <h1>{t("my_gear_title")}</h1>
          <p>{t("my_gear_page_subtitle")}</p>
        </div>
        <button
          type="button"
          className="my-gear-page-close"
          onClick={closeUtilityPage}
          aria-label={t("common_close")}
        >
          <X size={18} strokeWidth={2.5} aria-hidden="true" />
        </button>
      </header>

      {loadError ? <div className="my-gear-alert">{loadError}</div> : null}
      {actionError ? <div className="my-gear-alert">{actionError}</div> : null}

      {!formMode ? (
        <>
          <button
            type="button"
            className="my-gear-hero"
            onClick={() => startCreate("hero")}
          >
            <img
              src="/banner/mygear.png"
              alt=""
              aria-hidden="true"
              className="my-gear-hero__image my-gear-hero__image--light"
            />
            <img
              src="/banner/Darkmode/my-gear-hero-dark.png"
              alt=""
              aria-hidden="true"
              className="my-gear-hero__image my-gear-hero__image--dark"
            />
            <span className="my-gear-hero__plus" aria-hidden="true">
              <Plus size={24} strokeWidth={2.4} />
            </span>
            <span className="my-gear-hero__copy">
              <strong>{t("my_gear_hero_title")}</strong>
              <span>{t("my_gear_hero_subtitle")}</span>
            </span>
            <span className="my-gear-hero__art" aria-hidden="true">
              <Waves size={76} strokeWidth={1.4} />
              <GearIcon type="reel" className="my-gear-png-icon--hero-art" />
            </span>
          </button>

          <div className="my-gear-stats" aria-label={t("my_gear_stats_label")}>
            <div className="my-gear-stat-card">
              <GearIcon type="gearSet" />
              <strong>{gearStats.sets}</strong>
              <span>{t("my_gear_stats_sets")}</span>
            </div>
            <div className="my-gear-stat-card">
              <GearIcon type="rod" />
              <strong>{gearStats.rods}</strong>
              <span>{t("my_gear_filter_rods")}</span>
            </div>
            <div className="my-gear-stat-card">
              <GearIcon type="reel" />
              <strong>{gearStats.reels}</strong>
              <span>{t("my_gear_filter_reels")}</span>
            </div>
            <div className="my-gear-stat-card">
              <GearIcon type="tackleBox" />
              <strong>{gearStats.tackle}</strong>
              <span>{t("my_gear_stats_tackle_items")}</span>
            </div>
          </div>

          {SHOW_GEAR_FILTERS ? (
            <div className="my-gear-filters" aria-label={t("my_gear_filters_label")}>
              {CATEGORY_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  className={`my-gear-filter-pill${
                    activeFilter === filter.key ? " is-active" : ""
                  }`}
                  aria-pressed={activeFilter === filter.key}
                  onClick={() => setActiveFilter(filter.key)}
                >
                  {t(filter.labelKey)}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {formMode && isMobileViewport ? (
        <div className="my-gear-form-sheet-backdrop">{renderGearForm({ isSheet: true })}</div>
      ) : null}

      {formMode && !isMobileViewport ? renderGearForm() : null}

      {pendingDeleteGearSet ? (
        <div className="my-gear-confirm-backdrop" role="presentation">
          <section
            className="my-gear-confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="my-gear-delete-confirm-title"
            aria-describedby="my-gear-delete-confirm-message"
          >
            <h2 id="my-gear-delete-confirm-title">
              {t("my_gear_delete_confirm_title")}
            </h2>
            <p id="my-gear-delete-confirm-message">
              {t("my_gear_delete_confirm_message")}
            </p>
            <div className="my-gear-confirm__actions">
              <button
                type="button"
                className="my-gear-btn"
                onClick={closeDeleteConfirm}
                disabled={Boolean(deletingId)}
              >
                {t("no")}
              </button>
              <button
                type="button"
                className="my-gear-btn my-gear-btn--danger my-gear-btn--confirm-danger"
                onClick={confirmDelete}
                disabled={Boolean(deletingId)}
              >
                {t("yes_delete")}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {isLoading ? (
        <div className="my-gear-state">{t("loading")}</div>
      ) : gearSets.length === 0 && !formMode ? (
        <div className="my-gear-empty">
          <h2>{t("my_gear_empty_title")}</h2>
          <p>{t("my_gear_empty_body")}</p>
          <button
            type="button"
            className="my-gear-btn my-gear-btn--primary"
            onClick={() => startCreate("empty_state")}
          >
            {t("my_gear_add_set")}
          </button>
        </div>
      ) : !formMode && filteredGearSets.length === 0 ? (
        <div className="my-gear-state">{t("my_gear_filter_empty")}</div>
      ) : (
        <>
          <div className="my-gear-list">
            {filteredGearSets.map((gearSet) => {
              const visibleFields = getVisibleFields(gearSet);
              const chipFields = visibleFields.filter((field) => field.key !== "notes").slice(0, 3);
              return (
                <article key={gearSet.id} className="my-gear-card">
                  <span className="my-gear-card__icon" aria-hidden="true">
                    <GearIcon type="gearSet" className="my-gear-png-icon--card" />
                  </span>

                  <div className="my-gear-card__body">
                    <div className="my-gear-card__topline">
                      <h2>{gearSet.name}</h2>
                    </div>

                    {chipFields.length ? (
                      <div className="my-gear-card__chips">
                        {chipFields.map((field) => (
                          <span key={field.key}>{t(`my_gear_${field.key}`)}</span>
                        ))}
                      </div>
                    ) : null}

                    {visibleFields.length ? (
                      <dl className="my-gear-card__fields">
                        {visibleFields.map((field) => (
                          <div key={field.key}>
                            <dt>
                              <GearIcon type={FIELD_ICON_TYPES[field.key]} className="my-gear-png-icon--field" />
                              <span>{t(`my_gear_${field.key}`)}</span>
                            </dt>
                            <dd>{field.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>

                  <div className="my-gear-card__actions">
                    <button
                      type="button"
                      className="my-gear-btn my-gear-btn--danger"
                      disabled={deletingId === gearSet.id}
                      onClick={() => requestDelete(gearSet)}
                    >
                      <Trash2 size={15} strokeWidth={2.2} aria-hidden="true" />
                      {t("my_gear_delete_set")}
                    </button>
                    <button type="button" className="my-gear-btn" onClick={() => startEdit(gearSet)}>
                      <Pencil size={15} strokeWidth={2.2} aria-hidden="true" />
                      {t("my_gear_edit_set")}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {!formMode ? (
            <section className="my-gear-quick-access" aria-labelledby="my-gear-quick-access-title">
              <h2 id="my-gear-quick-access-title">{t("my_gear_quick_access")}</h2>
              <div className="my-gear-quick-access__grid">
                <button type="button" onClick={() => setActiveFilter("rod")}>
                  <GearIcon type="rod" />
                  <span>{t("my_gear_quick_rods")}</span>
                  <strong>{gearStats.rods}</strong>
                </button>
                <button type="button" onClick={() => setActiveFilter("reel")}>
                  <GearIcon type="reel" />
                  <span>{t("my_gear_quick_reels")}</span>
                  <strong>{gearStats.reels}</strong>
                </button>
                <button type="button" onClick={() => setActiveFilter("tackle")}>
                  <GearIcon type="line" />
                  <span>{t("my_gear_quick_lines")}</span>
                  <strong>{gearStats.lines}</strong>
                </button>
                <button type="button" onClick={() => setActiveFilter("tackle")}>
                  <GearIcon type="tackleBox" />
                  <span>{t("my_gear_quick_lures_tackle")}</span>
                  <strong>{gearStats.tackle}</strong>
                </button>
              </div>
            </section>
          ) : null}
        </>
      )}

      {!formMode ? (
        <div className="my-gear-page-actions">
          <button
            type="button"
            className="my-gear-btn my-gear-page-close-bottom"
            onClick={closeUtilityPage}
          >
            {t("common_close")}
          </button>
        </div>
      ) : null}
      </section>

      {desktopRightRail && desktopRightRailTarget
        ? createPortal(desktopRightRail, desktopRightRailTarget)
        : desktopRightRail && !desktopRightRailTargetId
          ? desktopRightRail
          : null}
    </div>
  );
}
